import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import Button from '../common/Button';
import Input from '../common/Input';
import Modal from '../common/Modal';
import { FieldError, MonthInput, NativeSelect, TextArea } from '../common/FormField';
import { useToast } from '../../contexts/ToastContext';
import { queryKeys } from '../../lib/queryClient';
import { api, ApiError, errorMessage } from '../../services/api';
import { BillingInvoice, Client, CompanyProfile, InvoicePreview, InvoiceType, TaxType } from '../../types';
import { currentMonth, fmtCurrency, fmtDate, fmtMonth, fmtNum, todayISO, toNum } from '../../utils/format';
import { compact, focusFirstError, hasErrors, MAX_MONEY, v } from '../../utils/validation';

interface ItemRow {
  key: number;
  title: string;
  description: string;
  hsnSac: string;
  quantity: string;
  unit: string;
  rate: string;
}

type ItemField = 'title' | 'description' | 'hsnSac' | 'quantity' | 'unit' | 'rate';
type ItemErrors = Partial<Record<ItemField, string>>;

type FormField = 'clientId' | 'billingMonth' | 'invoiceDate' | 'dueDate' | 'gstPercent' | 'periodStart' | 'periodEnd' | 'notes' | 'items';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  clients: Client[];
  company: CompanyProfile | null | undefined;
  onGenerated: (invoice: BillingInvoice) => void;
}

const MAX_ITEMS = 50;
let rowKey = 1;
const newRow = (hsnSac: string): ItemRow => ({ key: rowKey++, title: '', description: '', hsnSac, quantity: '1', unit: 'Nos', rate: '' });

function validateItem(row: ItemRow): ItemErrors {
  const amount = (toNum(row.quantity) ?? 0) * (toNum(row.rate) ?? 0);
  return compact({
    title: v.businessName(row.title, 'Title', true, 150, 3),
    description: v.text(row.description, 'Description', { max: 500 }),
    hsnSac: v.hsn(row.hsnSac),
    quantity: v.quantity(row.quantity),
    unit: v.unit(row.unit),
    rate: v.money(row.rate, 'Rate') ?? (amount > MAX_MONEY ? 'Line amount is too large' : undefined),
  });
}

const cellClass = (error?: string) =>
  `input-field text-sm ${error ? '!border-red-500 focus:!ring-red-500' : ''}`;

export default function GenerateInvoiceModal({ isOpen, onClose, clients, company, onGenerated }: Props) {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const defaultHsn = company?.defaultHsnSac ?? '998314';

  const [invoiceType, setInvoiceType] = useState<InvoiceType>('subscription');
  const [clientId, setClientId] = useState('');
  const [billingMonth, setBillingMonth] = useState(currentMonth());
  const [invoiceDate, setInvoiceDate] = useState(todayISO());
  const [dueDate, setDueDate] = useState('');
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [gstPercent, setGstPercent] = useState('18');
  const [taxType, setTaxType] = useState<'' | TaxType>('');
  const [items, setItems] = useState<ItemRow[]>([]);
  const [notes, setNotes] = useState('');
  const [errors, setErrors] = useState<Partial<Record<FormField, string>>>({});
  const [itemErrors, setItemErrors] = useState<Record<number, ItemErrors>>({});
  const [serverError, setServerError] = useState('');

  // Reset every time the modal opens.
  useEffect(() => {
    if (!isOpen) return;
    setInvoiceType('subscription');
    setClientId('');
    setBillingMonth(currentMonth());
    setInvoiceDate(todayISO());
    setDueDate('');
    setPeriodStart('');
    setPeriodEnd('');
    setGstPercent(String(company?.defaultGstPercent ?? 18));
    setTaxType('');
    setItems([]);
    setNotes('');
    setErrors({});
    setItemErrors({});
    setServerError('');
  }, [isOpen, company?.defaultGstPercent]);

  const clearError = (field: FormField) => {
    setErrors((p) => (p[field] ? { ...p, [field]: undefined } : p));
    setServerError('');
  };

  function switchType(type: InvoiceType) {
    setInvoiceType(type);
    setErrors({});
    setItemErrors({});
    setServerError('');
    // A custom invoice needs at least one line to start with.
    setItems(type === 'custom' ? [newRow(defaultHsn)] : []);
  }

  function updateItem(key: number, patch: Partial<ItemRow>) {
    setItems((rows) => rows.map((r) => (r.key === key ? { ...r, ...patch } : r)));
    // Clear only the errors of the fields being edited.
    setItemErrors((p) => {
      if (!p[key]) return p;
      const next = { ...p[key] };
      (Object.keys(patch) as ItemField[]).forEach((f) => delete next[f]);
      return { ...p, [key]: next };
    });
    clearError('items');
  }

  // The whole form, validated. Used for submit (shows errors) and — silently —
  // to decide whether the live preview can be requested.
  function validateForm(): { form: Partial<Record<FormField, string>>; lines: Record<number, ItemErrors> } {
    const lines: Record<number, ItemErrors> = {};
    for (const row of items) {
      const e = validateItem(row);
      if (hasErrors(e)) lines[row.key] = e;
    }
    const form = compact({
      clientId: clientId ? undefined : 'Client is required',
      billingMonth: invoiceType === 'subscription' ? v.month(billingMonth, 'Billing month') : undefined,
      invoiceDate: v.date(invoiceDate, 'Invoice date', { notFuture: true }),
      dueDate: v.date(dueDate, 'Due date', { required: false, min: invoiceDate || undefined, minLabel: 'the invoice date' }),
      gstPercent: v.gstPercent(gstPercent),
      periodStart:
        invoiceType === 'custom'
          ? v.date(periodStart, 'Period start', { required: false }) ?? (!periodStart && periodEnd ? 'Enter the period start too' : undefined)
          : undefined,
      periodEnd:
        invoiceType === 'custom'
          ? v.date(periodEnd, 'Period end', { required: false, min: periodStart || undefined, minLabel: 'the period start' }) ??
            (periodStart && !periodEnd ? 'Enter the period end too' : undefined)
          : undefined,
      notes: v.text(notes, 'Remark', { max: 500 }),
      items: invoiceType === 'custom' && items.length === 0 ? 'Add at least one line item' : undefined,
    });
    return { form, lines };
  }

  const payload = useMemo(() => {
    const { form, lines } = validateForm();
    if (hasErrors(form) || hasErrors(lines)) return null;
    return {
      clientId: Number(clientId),
      invoiceType,
      billingMonth: invoiceType === 'subscription' ? billingMonth : undefined,
      billingPeriodStart: invoiceType === 'custom' && periodStart ? periodStart : undefined,
      billingPeriodEnd: invoiceType === 'custom' && periodEnd ? periodEnd : undefined,
      invoiceDate,
      dueDate: dueDate || undefined,
      gstPercent: Number(gstPercent),
      taxType: taxType || undefined,
      items: items.map((r) => ({
        title: r.title.trim(),
        description: r.description.trim() || undefined,
        hsnSac: r.hsnSac,
        quantity: Number(r.quantity),
        unit: r.unit.trim(),
        rate: Number(r.rate),
      })),
      notes: notes.trim() || undefined,
    };
    // validateForm reads only the state listed here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, invoiceType, billingMonth, invoiceDate, dueDate, periodStart, periodEnd, gstPercent, taxType, items, notes]);

  // Live preview straight from the server's calculator, so what's shown is
  // exactly what will be saved (proration, IGST detection, round-off).
  const previewKey = JSON.stringify({ ...payload, notes: undefined });
  const { data: preview, error: previewError, isFetching } = useQuery({
    queryKey: ['billing', 'preview', previewKey],
    queryFn: () => api.post<InvoicePreview>('/billing/preview', payload),
    enabled: isOpen && !!payload,
    retry: false,
    staleTime: 10_000,
  });

  const generateMutation = useMutation({
    mutationFn: () => api.post<BillingInvoice>('/billing/generate', payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.billing.all() });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });

  // Maps "items.2.rate" style server errors back onto the right row.
  function applyServerErrors(err: unknown) {
    const fe = (err as ApiError)?.fieldErrors;
    if (!fe) return;
    const form: Partial<Record<FormField, string>> = {};
    const lines: Record<number, ItemErrors> = {};
    for (const [path, message] of Object.entries(fe)) {
      const m = path.match(/^items\.(\d+)\.(\w+)$/);
      if (m && items[Number(m[1])]) {
        const key = items[Number(m[1])].key;
        lines[key] = { ...lines[key], [m[2] as ItemField]: message };
      } else if (path === 'billingPeriodStart') form.periodStart = message;
      else if (path === 'billingPeriodEnd') form.periodEnd = message;
      else if (['clientId', 'billingMonth', 'invoiceDate', 'dueDate', 'gstPercent', 'notes', 'items'].includes(path)) {
        form[path as FormField] = message;
      }
    }
    setErrors(form);
    setItemErrors(lines);
  }

  async function handleGenerate() {
    const { form, lines } = validateForm();
    setErrors(form);
    setItemErrors(lines);
    setServerError('');
    if (hasErrors(form) || hasErrors(lines)) {
      focusFirstError(document.getElementById('generate-invoice-form'));
      return;
    }
    if (previewError) return setServerError(errorMessage(previewError));
    try {
      const invoice = await generateMutation.mutateAsync();
      showToast(`Invoice ${invoice.invoiceNumber} generated`, 'success');
      onGenerated(invoice);
    } catch (err) {
      applyServerErrors(err);
      setServerError(errorMessage(err, 'Failed to generate invoice'));
    }
  }

  const activeClients = clients.filter((c) => c.isActive);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Generate Invoice"
      size="xl"
      footer={
        <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="primary" icon="fa-file-invoice" onClick={handleGenerate} disabled={generateMutation.isPending}>
            {generateMutation.isPending ? 'Generating…' : 'Generate'}
          </Button>
        </div>
      }
    >
      <div id="generate-invoice-form" className="space-y-4">
        {/* Type toggle */}
        <div className="grid grid-cols-2 gap-2 p-1 bg-ink-100 rounded-lg">
          {([
            ['subscription', 'fa-rotate', 'Monthly Subscription', 'From Billing Configuration'],
            ['custom', 'fa-pen-to-square', 'Custom Invoice', 'One-off work, enter lines'],
          ] as const).map(([type, icon, label, hint]) => (
            <button
              key={type}
              type="button"
              onClick={() => switchType(type)}
              className={`rounded-md px-3 py-2 text-left transition-colors ${
                invoiceType === type ? 'bg-white shadow-soft text-brand-700' : 'text-ink-600 hover:text-ink-900'
              }`}
            >
              <span className="text-sm font-bold"><i className={`fas ${icon} mr-2 text-xs`} />{label}</span>
              <span className="block text-[11px] text-ink-500 mt-0.5">{hint}</span>
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <NativeSelect label="Client" required value={clientId} error={errors.clientId}
            onChange={(val) => { setClientId(val); clearError('clientId'); }}
            placeholder="Select client…" options={activeClients.map((c) => ({ value: c.id, label: c.companyName }))}
            helperText={activeClients.length === 0 ? 'No active clients — add one on the Clients page first' : undefined} />
          {invoiceType === 'subscription' ? (
            <MonthInput label="Billing Month" required value={billingMonth} error={errors.billingMonth}
              onChange={(val) => { setBillingMonth(val); clearError('billingMonth'); }} />
          ) : (
            <Input label="Invoice Date" type="date" required value={invoiceDate} max={todayISO()} error={errors.invoiceDate}
              onChange={(e) => { setInvoiceDate(e.target.value); clearError('invoiceDate'); }} />
          )}
        </div>

        {invoiceType === 'custom' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input label="Service Period Start" type="date" value={periodStart} error={errors.periodStart}
              onChange={(e) => { setPeriodStart(e.target.value); clearError('periodStart'); clearError('periodEnd'); }}
              helperText="Optional — printed as the billing period" />
            <Input label="Service Period End" type="date" value={periodEnd} min={periodStart || undefined} error={errors.periodEnd}
              onChange={(e) => { setPeriodEnd(e.target.value); clearError('periodEnd'); clearError('periodStart'); }} />
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {invoiceType === 'subscription' && (
            <Input label="Invoice Date" type="date" required value={invoiceDate} max={todayISO()} error={errors.invoiceDate}
              onChange={(e) => { setInvoiceDate(e.target.value); clearError('invoiceDate'); }} />
          )}
          <Input label="Due Date" type="date" value={dueDate} min={invoiceDate || undefined} error={errors.dueDate}
            onChange={(e) => { setDueDate(e.target.value); clearError('dueDate'); }}
            helperText={dueDate ? undefined : preview ? `Auto: ${fmtDate(preview.dueDate)}` : 'Auto from payment terms'} />
          <Input label="GST %" type="number" required value={gstPercent} maxLength={5} error={errors.gstPercent}
            onChange={(e) => { setGstPercent(e.target.value); clearError('gstPercent'); }} helperText="0 to 28" />
          <NativeSelect label="Tax Type" value={taxType} onChange={(val) => setTaxType(val as '' | TaxType)}
            placeholder="Auto-detect from GSTIN"
            options={[
              { value: 'intra', label: 'Same state — CGST + SGST' },
              { value: 'inter', label: 'Other state — IGST' },
            ]} />
        </div>

        {/* Line items */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold uppercase tracking-[0.06em] text-ink-600">
              {invoiceType === 'subscription' ? 'Extra one-off lines (optional)' : 'Line Items'}
              {invoiceType === 'custom' && <span className="text-red-500 ml-1">*</span>}
            </span>
            <button type="button" className="text-sm text-brand-600 hover:text-brand-800 font-medium disabled:opacity-40"
              disabled={items.length >= MAX_ITEMS}
              onClick={() => { setItems((rows) => [...rows, newRow(defaultHsn)]); clearError('items'); }}>
              <i className="fas fa-plus text-xs mr-1" />Add line
            </button>
          </div>
          <FieldError error={errors.items} />
          <div className="space-y-2">
            {items.map((row, idx) => {
              const e = itemErrors[row.key] ?? {};
              const messages = Object.values(e).filter(Boolean);
              return (
                <div key={row.key} className={`p-3 bg-ink-50 rounded-lg border ${messages.length ? 'border-red-300' : 'border-ink-200'}`}>
                  <div className="grid grid-cols-12 gap-2">
                    <input className={`col-span-12 sm:col-span-5 ${cellClass(e.title)}`} placeholder="Title * (e.g. Custom Development)" maxLength={150}
                      aria-label={`Line ${idx + 1} title`} aria-invalid={e.title ? true : undefined}
                      value={row.title} onChange={(ev) => updateItem(row.key, { title: ev.target.value })} />
                    <input className={`col-span-12 sm:col-span-7 ${cellClass(e.description)}`} placeholder="Description (optional)" maxLength={500}
                      aria-label={`Line ${idx + 1} description`} aria-invalid={e.description ? true : undefined}
                      value={row.description} onChange={(ev) => updateItem(row.key, { description: ev.target.value })} />
                    <input className={`col-span-4 sm:col-span-2 ${cellClass(e.hsnSac)}`} placeholder="HSN/SAC *" maxLength={8} inputMode="numeric"
                      aria-label={`Line ${idx + 1} HSN/SAC`} aria-invalid={e.hsnSac ? true : undefined}
                      value={row.hsnSac} onChange={(ev) => updateItem(row.key, { hsnSac: ev.target.value.replace(/\D/g, '') })} />
                    <input className={`col-span-4 sm:col-span-2 ${cellClass(e.quantity)}`} placeholder="Qty *" inputMode="decimal" maxLength={11}
                      aria-label={`Line ${idx + 1} quantity`} aria-invalid={e.quantity ? true : undefined}
                      value={row.quantity} onChange={(ev) => updateItem(row.key, { quantity: ev.target.value.replace(/[^0-9.]/g, '') })} />
                    <input className={`col-span-4 sm:col-span-2 ${cellClass(e.unit)}`} placeholder="Unit *" maxLength={30}
                      aria-label={`Line ${idx + 1} unit`} aria-invalid={e.unit ? true : undefined}
                      value={row.unit} onChange={(ev) => updateItem(row.key, { unit: ev.target.value })} />
                    <input className={`col-span-6 sm:col-span-3 ${cellClass(e.rate)}`} placeholder="Rate (₹) *" inputMode="decimal" maxLength={13}
                      aria-label={`Line ${idx + 1} rate`} aria-invalid={e.rate ? true : undefined}
                      value={row.rate} onChange={(ev) => updateItem(row.key, { rate: ev.target.value.replace(/[^0-9.]/g, '') })} />
                    <div className="col-span-4 sm:col-span-2 flex items-center justify-end text-sm font-semibold text-ink-800">
                      {fmtCurrency((toNum(row.quantity) ?? 0) * (toNum(row.rate) ?? 0))}
                    </div>
                    <button type="button" title="Remove line" aria-label={`Remove line ${idx + 1}`}
                      className="col-span-2 sm:col-span-1 flex items-center justify-center text-ink-400 hover:text-red-600"
                      onClick={() => {
                        setItems((rows) => rows.filter((r) => r.key !== row.key));
                        setItemErrors((p) => { const { [row.key]: _removed, ...rest } = p; return rest; });
                      }}>
                      <i className="fas fa-trash-can" />
                    </button>
                  </div>
                  {messages.length > 0 && (
                    <ul className="mt-2 space-y-0.5" role="alert">
                      {messages.map((m) => (
                        <li key={m} className="text-xs text-red-600"><i className="fas fa-circle-exclamation mr-1" />{m}</li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <TextArea label="Remark (printed on invoice)" value={notes} maxLength={500} placeholder="Optional…" error={errors.notes}
          onChange={(val) => { setNotes(val); clearError('notes'); }} />

        {/* Live preview */}
        <div className="rounded-lg border border-ink-200 overflow-hidden">
          <div className="bg-ink-50 border-b border-ink-200 px-4 py-2 flex items-center justify-between">
            <span className="text-xs font-semibold text-ink-500 uppercase tracking-wider">
              Preview{invoiceType === 'subscription' && billingMonth ? ` — ${fmtMonth(billingMonth)}` : ''}
            </span>
            {isFetching && <i className="fas fa-spinner fa-spin text-ink-400 text-xs" />}
          </div>
          {!payload && (
            <p className="px-4 py-6 text-sm text-ink-400 text-center">
              {clientId ? 'Complete the fields above to see the invoice preview.' : 'Select a client to see the invoice preview.'}
            </p>
          )}
          {payload && previewError && (
            <p className="px-4 py-4 text-sm text-red-600"><i className="fas fa-circle-exclamation mr-1.5" />{errorMessage(previewError)}</p>
          )}
          {payload && preview && !previewError && (
            <div className="p-4">
              <table className="w-full text-sm">
                <tbody>
                  {preview.lines.map((l, i) => (
                    <tr key={i} className="border-b border-ink-100 last:border-0">
                      <td className="py-1.5 pr-3">
                        <span className="font-medium text-ink-800">{l.title}</span>
                        {l.description && <span className="block text-xs text-ink-500">{l.description}</span>}
                      </td>
                      <td className="py-1.5 px-2 text-right text-xs text-ink-500 whitespace-nowrap">
                        {fmtNum(l.quantity)} {l.unit} × {fmtCurrency(l.rate)}
                      </td>
                      <td className="py-1.5 pl-2 text-right font-mono text-ink-900 whitespace-nowrap">{fmtCurrency(l.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="mt-3 pt-3 border-t border-ink-200 space-y-1 text-sm max-w-xs ml-auto">
                <TotalRow label="Subtotal" value={fmtCurrency(preview.subtotal)} />
                {preview.taxType === 'inter' ? (
                  <TotalRow label={`IGST @ ${preview.igstPercent}%`} value={fmtCurrency(preview.igstAmount)} />
                ) : (
                  <>
                    <TotalRow label={`CGST @ ${preview.cgstPercent}%`} value={fmtCurrency(preview.cgstAmount)} />
                    <TotalRow label={`SGST @ ${preview.sgstPercent}%`} value={fmtCurrency(preview.sgstAmount)} />
                  </>
                )}
                {preview.roundOff !== 0 && <TotalRow label="Round off" value={preview.roundOff.toFixed(2)} />}
                <div className="flex justify-between pt-1.5 border-t border-ink-200 text-base font-bold text-ink-900">
                  <span>Gross Total</span>
                  <span className="font-mono">{fmtCurrency(preview.totalAmount)}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {serverError && (
          <div role="alert" className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
            <i className="fas fa-circle-exclamation mr-1.5" />{serverError}
          </div>
        )}
      </div>
    </Modal>
  );
}

function TotalRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between text-ink-600">
      <span>{label}</span>
      <span className="font-mono text-ink-800">{value}</span>
    </div>
  );
}
